import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DemoSession } from '@/data/demoSession';
import type { WorkoutLogApi } from '@/lib/useWorkoutLog';
import { formatVolume, loadIncrement, formatWeight } from '@/lib/units';
import { MOOD_LABELS, formatPrescription, type PrescriptionLine } from '@/lib/prescription';
import { cn } from '@/lib/format';
import { CheckIcon, PlusIcon, SparkIcon, SyncIcon, UndoIcon, BarbellIcon } from '@/lib/icons';
import { SessionTotals } from './SessionTotals';
import { SetCell, type SetDraftPatch } from './SetCell';
import { Button } from './ui';

/** Adapt a grid row to the shared prescription vocabulary so formatting is identical everywhere. */
function toLine(row: WorkoutGridProps['api']['rows'][number]): PrescriptionLine {
  return {
    supersetGroup: row.supersetGroup,
    prescribedSets: row.prescribed.sets,
    repMin: row.prescribed.repMin,
    repMax: row.prescribed.repMax,
    measure: row.measure,
    perSide: row.perSide,
    isBodyweight: row.isBodyweight,
    tempo: row.tempo,
    targetRpe: row.prescribed.targetRpe,
    percent1rm: null,
    targetWeightKg: row.prescribed.targetWeightKg,
    targetDistanceM: row.prescribed.targetDistanceM,
    targetDurationS: null,
    restSeconds: row.prescribed.restSeconds,
  };
}

export interface WorkoutGridProps {
  api: WorkoutLogApi;
  session: DemoSession;
  className?: string;
}

/**
 * THE GRID — the specialized interactive logging table that replaces the
 * raw spreadsheet (UI/UX spec §4).
 *
 * Real behaviour, no mock-ups: cells edit in place, Enter commits, `d` toggles
 * done, `p` stages a PR attempt, `Delete` removes a set, arrows move between
 * cells, ⌘/Ctrl+Z undoes, and PR detection runs the same Epley/rep-bucket maths
 * the database trigger uses.
 */
export function WorkoutGrid({ api, session, className }: WorkoutGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  const columnCount = useMemo(
    () => Math.max(1, ...api.rows.map((row) => Math.max(row.sets.length, row.prescribed.sets))),
    [api.rows],
  );

  const cellKey = (rowId: string, column: number) => `${rowId}:${column}`;

  const focusCell = useCallback(
    (rowIndex: number, column: number) => {
      const row = api.rows[rowIndex];
      if (!row) return;
      const el = containerRef.current?.querySelector<HTMLElement>(`[data-cell="${rowIndex}-${column}"]`);
      if (!el) return;
      el.focus();
      setFocused(cellKey(row.id, column));
    },
    [api.rows],
  );

  const navigate = useCallback(
    (rowIndex: number, column: number, dir: 'left' | 'right' | 'up' | 'down') => {
      let nextRow = rowIndex;
      let nextColumn = column;
      if (dir === 'left') nextColumn = Math.max(0, column - 1);
      if (dir === 'right') nextColumn = Math.min(columnCount - 1, column + 1);
      if (dir === 'up') nextRow = Math.max(0, rowIndex - 1);
      if (dir === 'down') nextRow = Math.min(api.rows.length - 1, rowIndex + 1);
      focusCell(nextRow, nextColumn);
    },
    [api.rows.length, columnCount, focusCell],
  );

  // PR celebration — auto-dismissed, announced assertively
  useEffect(() => {
    if (!api.lastPr) return;
    const timer = window.setTimeout(() => api.clearPr(), 5000);
    return () => window.clearTimeout(timer);
  }, [api.lastPr, api.clearPr]);

  // ⌘Z / Ctrl+Z is global: after a commit the focus sits on the cell, but the
  // shortcut must still work while the pointer is anywhere on the page.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'z' || event.key === 'Z')) {
        event.preventDefault();
        api.undo();
      } else if (event.key === 'Escape') {
        setEditing(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [api.undo]);

  const addSetToEveryRow = () => {
    for (const row of api.rows) api.addSet(row.id, 1);
  };

  return (
    <div className={cn('flex min-w-0 flex-col gap-0 overflow-hidden rounded-card border border-hairline bg-surface shadow-card', className)}>
      {/* ---------------- toolbar ---------------- */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-surface-raised px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center bg-brand-charcoal text-brand-white">
            <BarbellIcon className="text-base" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-[15px] font-black uppercase tracking-[-0.01em] text-ink">
              {session.label}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
              Week {session.week} · Day {session.dayIndex}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="hidden items-center gap-1.5 rounded-pill border border-info/50 bg-info/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-info sm:inline-flex">
            <SyncIcon className="text-xs" />
            Runs in your browser · nothing is uploaded
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={api.toggleUnit}
            aria-label={`Switch to ${api.unit === 'metric' ? 'pounds' : 'kilograms'}`}
          >
            {api.unit === 'metric' ? 'kg' : 'lb'} ⇄ {api.unit === 'metric' ? 'lb' : 'kg'}
          </Button>
          <Button variant="outline" size="sm" onClick={addSetToEveryRow}>
            <PlusIcon className="text-xs" /> Set on every exercise
          </Button>
          <Button variant="ghost" size="sm" onClick={api.undo} disabled={!api.canUndo}>
            <UndoIcon className="text-xs" /> Undo
          </Button>
          <Button variant="ghost" size="sm" onClick={api.reset}>
            Reset demo
          </Button>
        </div>
      </div>

      {/* ---------------- live totals ---------------- */}
      <div className="border-b border-hairline px-4 py-4">
        <SessionTotals totals={api.totals} compliancePct={api.compliancePct} unit={api.unit} />
      </div>

      {/* ---------------- the table ---------------- */}
      <div
        ref={containerRef}
        className="l2f-grid-scroll"
      >
        <table className="w-full min-w-[900px] border-separate border-spacing-0 text-left">
          <caption className="l2f-visually-hidden">
            Live set log for {session.label}, week {session.week}. Each cell is an editable set: Enter opens it, d marks
            it done, p stages a personal-record attempt, Delete removes it, and the arrow keys move between cells.
          </caption>
          <thead>
            <tr>
              <th scope="col" className="l2f-grid-corner w-[268px] border-b border-hairline px-3 py-2.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Exercise</span>
              </th>
              {Array.from({ length: columnCount }, (_, column) => (
                <th
                  key={column}
                  scope="col"
                  className="l2f-grid-head w-[116px] border-b border-l border-hairline px-3 py-2.5"
                >
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Set {column + 1}</span>
                </th>
              ))}
              <th scope="col" className="l2f-grid-head w-[56px] border-b border-l border-hairline px-2 py-2.5">
                <span className="sr-only">Add a set</span>
              </th>
              <th scope="col" className="l2f-grid-head min-w-[220px] border-b border-l border-hairline px-3 py-2.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Notes &amp; coach cue</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {api.rows.map((row, rowIndex) => {
              const increment = loadIncrement(row.equipment, api.unit);
              const isRowActive = focused?.startsWith(`${row.id}:`) ?? false;
              return (
                <tr key={row.id} className={cn('align-top', isRowActive && 'bg-accent/[0.035]')}>
                  <th
                    scope="row"
                    className="l2f-grid-col relative border-b border-hairline px-3 py-3 text-left align-top"
                  >
                    <div className="flex flex-col gap-1.5">
                      <span className="flex items-center gap-2">
                        {isRowActive ? (
                          <span aria-hidden="true" className="absolute left-0 top-0 h-full w-[2px] bg-accent" />
                        ) : null}
                        <span className="text-[14px] font-bold leading-tight text-ink">{row.name}</span>
                      </span>
                      <span className="l2f-tabular font-mono text-[11px] text-ink-muted">
                        {formatPrescription(toLine(row), api.unit)}
                      </span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {row.supersetGroup !== null ? (
                          <span className="rounded-full bg-accent px-1.5 py-[1px] text-[9px] font-black uppercase tracking-[0.06em] text-on-accent">
                            group {row.supersetGroup}
                          </span>
                        ) : null}
                        {row.tempo ? (
                          <span className="rounded-sm border border-hairline px-1.5 py-[1px] font-mono text-[10px] font-bold text-ink-muted">
                            tempo {row.tempo}
                          </span>
                        ) : null}
                        {row.modifications ? (
                          <span className="rounded-sm border border-info/40 bg-info/10 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.06em] text-info">
                            {row.modifications}
                          </span>
                        ) : null}
                        {row.perSide ? (
                          <span className="rounded-sm border border-accent/40 bg-accent/10 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.06em] text-accent">
                            per side
                          </span>
                        ) : null}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                        {row.muscleGroup} · {row.equipment} · +{increment}
                        {api.unit === 'metric' ? ' kg' : ' lb'} per step
                      </span>
                      {row.coachCue ? (
                        <span className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-accent">
                          <SparkIcon className="mt-[2px] shrink-0 text-[11px]" />
                          <span>{row.coachCue}</span>
                        </span>
                      ) : null}
                    </div>
                  </th>

                  {Array.from({ length: columnCount }, (_, column) => {
                    const set = row.sets[column];
                    if (!set) {
                      const withinPrescription = column < row.prescribed.sets;
                      const emptyLabel = withinPrescription ? `Log set ${column + 1}` : `Extra set ${column + 1}`;
                      return (
                        <td key={`empty-${column}`} className="border-b border-l border-hairline p-0 align-top">
                          <button
                            type="button"
                            data-cell={`${rowIndex}-${column}`}
                            tabIndex={focused === cellKey(row.id, column) ? 0 : -1}
                            onClick={() => {
                              api.addSet(row.id, 1);
                              setFocused(cellKey(row.id, column));
                            }}
                            onFocus={() => setFocused(cellKey(row.id, column))}
                            aria-label={`${emptyLabel} of ${row.name}`}
                            className="flex h-full min-h-[64px] w-full flex-col items-center justify-center gap-1 border border-dashed border-hairline text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted transition-colors duration-150 hover:border-accent hover:text-accent focus-visible:border-accent focus-visible:text-accent"
                          >
                            <PlusIcon className="text-sm" />
                            {emptyLabel}
                          </button>
                        </td>
                      );
                    }

                    const key = cellKey(row.id, column);
                    return (
                      <td key={set.id} className="border-b border-l border-hairline p-0 align-top">
                        <SetCell
                          row={row}
                          set={set}
                          setNumber={column + 1}
                          unit={api.unit}
                          cellId={`${rowIndex}-${column}`}
                          editing={editing === `${row.id}:${set.id}`}
                          isFocused={focused === key}
                          onStartEdit={() => {
                            setEditing(`${row.id}:${set.id}`);
                            setFocused(key);
                          }}
                          onStopEdit={() => setEditing(null)}
                          onField={(patch: SetDraftPatch) => api.setFields(row.id, set.id, patch)}
                          onCommit={(patch?: SetDraftPatch) => api.commitSet(row.id, set.id, patch)}
                          onDelete={() => {
                            api.deleteSet(row.id, set.id);
                            setEditing(null);
                          }}
                          onToggleDone={() =>
                            api.setStatus(row.id, set.id, set.status === 'pending' ? 'done' : 'pending')
                          }
                          onPrAttempt={() =>
                            api.commitSet(row.id, set.id, {
                              weightKg: set.weightKg + loadIncrement(row.equipment, api.unit),
                            })
                          }
                          onNavigate={(dir) => navigate(rowIndex, column, dir)}
                        />
                      </td>
                    );
                  })}

                  <td className="border-b border-l border-hairline p-0 align-top">
                    <button
                      type="button"
                      onClick={() => api.addSet(row.id, 1)}
                      aria-label={`Add another set to ${row.name}`}
                      className="flex h-full min-h-[64px] w-full items-center justify-center text-ink-muted transition-colors duration-150 hover:bg-page-subtle hover:text-accent"
                    >
                      <PlusIcon className="text-sm" />
                    </button>
                  </td>

                  <td className="border-b border-l border-hairline p-2 align-top">
                    <label className="sr-only" htmlFor={`notes-${row.id}`}>
                      Notes for {row.name}
                    </label>
                    <textarea
                      id={`notes-${row.id}`}
                      rows={2}
                      maxLength={140}
                      value={row.notes}
                      onChange={(event) => api.setNote(row.id, event.target.value)}
                      placeholder="Bar speed, cues, niggles…"
                      className="w-full resize-none rounded-none border border-hairline bg-page-subtle px-2 py-1.5 text-[12px] leading-snug text-ink placeholder:text-ink-muted"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---------------- post-session score (the sheet's POST SESSION SCORE) ---------------- */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-hairline bg-page-subtle px-4 py-3">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Post session score</span>

          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Mood</span>
            <div role="group" aria-label="Session mood, 1 to 5" className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((score) => (
                <button
                  key={score}
                  type="button"
                  aria-pressed={api.postSession.mood === score}
                  onClick={() => api.setPostSession({ mood: score })}
                  title={MOOD_LABELS[score]}
                  className={cn(
                    'h-7 w-7 border text-[11px] font-bold transition-colors duration-150',
                    api.postSession.mood === score
                      ? 'border-accent bg-accent text-on-accent'
                      : 'border-hairline bg-surface text-ink-muted hover:border-accent hover:text-accent',
                  )}
                >
                  {score}
                </button>
              ))}
            </div>
            {api.postSession.mood !== null ? (
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink">
                {MOOD_LABELS[api.postSession.mood]}
              </span>
            ) : null}
          </div>

          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Session RPE</span>
            <select
              value={api.postSession.rpe === null ? '' : String(api.postSession.rpe)}
              onChange={(event) =>
                api.setPostSession({ rpe: event.target.value === '' ? null : Number.parseFloat(event.target.value) })
              }
              className="l2f-tabular rounded-none border border-hairline bg-surface px-1.5 py-1 font-mono text-[12px] font-bold text-ink"
            >
              <option value="">—</option>
              {['5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10'].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Pain</span>
            <select
              value={String(api.postSession.pain ?? 0)}
              onChange={(event) => api.setPostSession({ pain: Number.parseFloat(event.target.value) })}
              className={cn(
                'l2f-tabular rounded-none border bg-surface px-1.5 py-1 font-mono text-[12px] font-bold',
                (api.postSession.pain ?? 0) >= 4 ? 'border-danger text-danger' : 'border-hairline text-ink',
              )}
            >
              {Array.from({ length: 11 }, (_, index) => String(index)).map((value) => (
                <option key={value} value={value}>
                  {value} / 10
                </option>
              ))}
            </select>
          </label>
        </div>
        <span className="text-[10px] text-ink-muted">Three taps — the whole post-session form from the sheet.</span>
      </div>

      {/* ---------------- footer actions ---------------- */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-4 py-3">
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-muted">
          <span>
            <Kbd>Enter</Kbd> edit · commit
          </span>
          <span>
            <Kbd>D</Kbd> done
          </span>
          <span>
            <Kbd>P</Kbd> PR attempt
          </span>
          <span>
            <Kbd>Del</Kbd> remove
          </span>
          <span>
            <Kbd>↑↓←→</Kbd> move
          </span>
          <span>
            <Kbd>⌘Z</Kbd> undo
          </span>
        </p>
        <Button size="md" onClick={() => setFinished(true)} disabled={!api.hasCommitted}>
          <CheckIcon className="text-sm" /> Finish session
        </Button>
      </div>

      {/* ---------------- completion panel ---------------- */}
      {finished ? (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline border-l-4 border-l-accent bg-surface px-4 py-4 text-ink"
        >
          <div className="flex items-center gap-3">
            <CheckIcon className="text-xl text-success" />
            <div className="flex flex-col">
              <span className="font-display text-[16px] font-black uppercase tracking-[-0.01em]">
                Session logged · {api.totals.setsCompleted} sets · {formatVolume(api.totals.volumeKg, api.unit)}
              </span>
              <span className="text-[12px] text-ink-muted">
                {api.totals.prCount > 0
                  ? `${api.totals.prCount} personal record${api.totals.prCount === 1 ? '' : 's'} today. Premium members get the AI coach's rewrite of next week from this exact log.`
                  : 'Premium members get their AI coach\u2019s rewrite of next week from this exact log.'}
              </span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setFinished(false)} className="border-hairline text-ink">
            Back to the Grid
          </Button>
        </div>
      ) : null}

      {/* ---------------- PR toast ---------------- */}
      {api.lastPr ? (
        <div
          aria-live="assertive"
          className="pointer-events-none fixed bottom-6 right-6 z-[80] flex items-center gap-3 border-l-4 border-accent bg-brand-charcoal px-4 py-3 text-brand-white shadow-modal"
        >
          <span className="font-display text-2xl font-black leading-none text-accent">▲</span>
          <div className="flex flex-col">
            <span className="text-label font-bold uppercase tracking-[0.08em] text-accent">Personal record</span>
            <span className="l2f-tabular font-mono text-[13px] font-bold">
              {formatWeight(api.lastPr.weightKg, api.unit)} × {api.lastPr.reps} · {api.lastPr.exerciseName}
            </span>
            <span className="text-[11px] text-brand-white/70">
              e1RM {api.lastPr.e1rm.toFixed(1)} kg
              {api.lastPr.previousBest !== null ? ` · +${api.lastPr.deltaKg.toFixed(1)} kg on your previous best` : ''}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded-sm border border-hairline bg-page-subtle px-1.5 py-[1px] font-mono text-[10px] font-bold text-ink">
      {children}
    </kbd>
  );
}
